import { useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';

import { CreateAccountRecommendationDocument } from '../graphql/mutation.createAccountRecommendation.generated';
import { AccountRecommendationOptionQuery } from '../graphql/query.accountRecommendationOption.generated';

type AccountRecommendationOption = NonNullable<AccountRecommendationOptionQuery['accountRecommendationOption']>;
type ProductOnboardingOption = AccountRecommendationOption['recommendedProductOnboardingOption'];
type RecommendationState = { option: ProductOnboardingOption; isSkipEligible: boolean; reasoning: string | null };

export const useAccountRecommendation = (props: { initialRecommendationOption: AccountRecommendationOption }) => {
  const { initialRecommendationOption } = props;
  const [currentRecommendation, setCurrentRecommendation] = useState<RecommendationState>({
    option: initialRecommendationOption.recommendedProductOnboardingOption,
    isSkipEligible: initialRecommendationOption.isRecommendedOptionSkipEligible,
    reasoning: null,
  });
  const [skippedRecommendations, setSkippedRecommendations] = useState<RecommendationState[]>([]);
  const [isCreationLoading, setIsCreationLoading] = useState<boolean>(false);
  const [isSkipLoading, setIsSkipLoading] = useState<boolean>(false);
  const [currentError, setError] = useState<Error | null>(null);
  const [mutate] = useGraphQLMutation(CreateAccountRecommendationDocument);

  if (currentError) {
    throw currentError;
  }

  const createAccountRecommendation = async ({ skipCurrent = false }: { skipCurrent: boolean }) => {
    const setLoading = skipCurrent ? setIsSkipLoading : setIsCreationLoading;
    setLoading(true);
    try {
      const optionsToSkip = skipCurrent ? [currentRecommendation, ...skippedRecommendations] : skippedRecommendations;
      const skippedProductOnboardingOptions = optionsToSkip.map(s => s.option.id);
      const mutationResult = await mutate({ variables: { skippedProductOnboardingOptions } });
      const result = mutationResult.data?.createAccountRecommendation.result;
      if (result) {
        if (skipCurrent) {
          setSkippedRecommendations([...skippedRecommendations, currentRecommendation]);
        }
        setCurrentRecommendation({
          option: result.productOnboardingOption,
          isSkipEligible: result.isOptionSkipEligible,
          reasoning: result.reasoning,
        });
      }
    } catch (error) {
      setError(Error(`Mutation failed: ${error?.toString()}.`));
    } finally {
      setLoading(false);
    }
  };

  return {
    currentRecommendation,
    isCreationLoading,
    isSkipLoading,
    createAccountRecommendation: async () => createAccountRecommendation({ skipCurrent: false }),
    skipAccountRecommendation: async () => createAccountRecommendation({ skipCurrent: true }),
  };
};
